
import React, { useState } from 'react';
import { Ambassador, ApplicationStatus } from '../types';

interface CreateAmbassadorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (ambassador: Ambassador) => void;
}

export const CreateAmbassadorModal: React.FC<CreateAmbassadorModalProps> = ({ isOpen, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    instagramHandle: '',
    tiktokHandle: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    reason: '',
    previousExperience: ''
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email) {
      alert("First Name, Last Name, and Email are required.");
      return;
    }

    const newAmbassador: Ambassador = {
      id: `manual-${Math.random().toString(36).substr(2, 9)}`,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone || 'N/A',
      location: formData.city && formData.state ? `${formData.city}, ${formData.state}` : (formData.city || formData.country || 'Remote'),
      onboardingScore: 0,
      status: ApplicationStatus.NEW,
      appliedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      reason: formData.reason || 'Manual entry',
      typeformUrl: 'manual',
      instagramHandle: formData.instagramHandle,
      tiktokHandle: formData.tiktokHandle,
      previousExperience: formData.previousExperience,
      address: {
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: formData.country
      },
      additionalFields: [
        { label: 'Why?', value: formData.reason },
        { label: 'Experience', value: formData.previousExperience },
        { label: 'Insta', value: formData.instagramHandle },
        { label: 'TikTok', value: formData.tiktokHandle }
      ]
    };

    onCreate(newAmbassador);
    onClose();
    // Reset form
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      instagramHandle: '',
      tiktokHandle: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      reason: '',
      previousExperience: ''
    });
  };

  return (
    <div 
      className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-2xl bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-10 py-8 border-b border-purple-50 flex justify-between items-center bg-purple-50/50">
          <div>
            <h2 className="text-xl font-bold text-purple-900 tracking-tight">Manual Onboarding</h2>
            <p className="text-[10px] font-black uppercase tracking-widest text-purple-400 mt-1">Direct Database Entry</p>
          </div>
          <button onClick={onClose} className="p-2 bg-white rounded-xl text-gray-400 hover:text-gray-900 transition-all shadow-sm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-10 space-y-8 scrollbar-hide">
          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">First Name</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">Last Name</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">Email Address</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">Instagram Handle</label>
              <input
                type="text"
                placeholder="@username"
                value={formData.instagramHandle}
                onChange={(e) => setFormData({ ...formData, instagramHandle: e.target.value })}
                className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
              />
            </div>
            <div className="col-span-1">
              <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">TikTok Handle</label>
              <input
                type="text"
                placeholder="@username"
                value={formData.tiktokHandle}
                onChange={(e) => setFormData({ ...formData, tiktokHandle: e.target.value })}
                className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
              />
            </div>
          </div>

          <div className="h-[1px] bg-purple-50"></div>

          <div>
             <h3 className="text-xs font-black uppercase tracking-widest text-gray-900 mb-6">Address Information</h3>
             <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6">
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">Street</label>
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">State</label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">Zip</label>
                  <input
                    type="text"
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
                  />
                </div>
             </div>
          </div>

          <div className="h-[1px] bg-purple-50"></div>

          <div className="space-y-6">
             <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">Why do they want to be an ambassador?</label>
                <textarea
                  rows={3}
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
                />
             </div>
             <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-1">Previous Experience</label>
                <textarea
                  rows={2}
                  value={formData.previousExperience}
                  onChange={(e) => setFormData({ ...formData, previousExperience: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs focus:ring-2 focus:ring-purple-500 focus:bg-white outline-none transition-all"
                />
             </div>
          </div>
        </form>

        <div className="px-10 py-8 bg-gray-50/50 border-t border-purple-50 flex gap-4">
           <button onClick={onClose} type="button" className="flex-1 bg-white border border-gray-100 text-gray-500 rounded-full py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-gray-100 transition-all">Cancel</button>
           <button onClick={handleSubmit} type="submit" className="flex-1 bg-purple-600 text-white rounded-full py-4 text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-purple-100 hover:bg-purple-500 transition-all">Add Ambassador</button>
        </div>
      </div>
    </div>
  );
};
